import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function OptionGreeksCalculator() {
  const [spotPrice, setSpotPrice] = useState("");
  const [strikePrice, setStrikePrice] = useState("");
  const [timeToExpiry, setTimeToExpiry] = useState("");
  const [volatility, setVolatility] = useState("");
  const [riskFreeRate, setRiskFreeRate] = useState("");
  const [optionType, setOptionType] = useState("call");
  const [result, setResult] = useState<{
    delta: number;
    gamma: number;
    theta: number;
    vega: number;
    rho: number;
  } | null>(null);

  // Standard normal cumulative distribution function
  const normCDF = (x: number) => {
    const t = 1 / (1 + 0.2316419 * Math.abs(x));
    const d = 0.3989423 * Math.exp(-x * x / 2);
    const p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
    return x > 0 ? 1 - p : p;
  };

  // Standard normal probability density function
  const normPDF = (x: number) => {
    return Math.exp(-x * x / 2) / Math.sqrt(2 * Math.PI);
  };

  const calculate = () => {
    const S = parseFloat(spotPrice);
    const K = parseFloat(strikePrice);
    const T = parseFloat(timeToExpiry) / 365; // Convert days to years
    const sigma = parseFloat(volatility) / 100;
    const r = parseFloat(riskFreeRate) / 100;

    if (isNaN(S) || isNaN(K) || isNaN(T) || isNaN(sigma) || isNaN(r) ||
        S <= 0 || K <= 0 || T <= 0 || sigma <= 0) {
      toast.error("Please enter valid values");
      return;
    }

    // Black-Scholes d1 and d2
    const d1 = (Math.log(S / K) + (r + sigma * sigma / 2) * T) / (sigma * Math.sqrt(T));
    const d2 = d1 - sigma * Math.sqrt(T);

    // Calculate Greeks
    const delta = optionType === "call" ? normCDF(d1) : normCDF(d1) - 1;
    const gamma = normPDF(d1) / (S * sigma * Math.sqrt(T));
    const vega = S * normPDF(d1) * Math.sqrt(T) / 100; // Scaled by 1%
    const theta = optionType === "call"
      ? (-(S * normPDF(d1) * sigma) / (2 * Math.sqrt(T)) - r * K * Math.exp(-r * T) * normCDF(d2)) / 365
      : (-(S * normPDF(d1) * sigma) / (2 * Math.sqrt(T)) + r * K * Math.exp(-r * T) * normCDF(-d2)) / 365;
    const rho = optionType === "call"
      ? K * T * Math.exp(-r * T) * normCDF(d2) / 100
      : -K * T * Math.exp(-r * T) * normCDF(-d2) / 100;

    setResult({ delta, gamma, theta, vega, rho });
    toast.success("Option Greeks calculated!");
  };

  const reset = () => {
    setSpotPrice("");
    setStrikePrice("");
    setTimeToExpiry("");
    setVolatility("");
    setRiskFreeRate("");
    setOptionType("call");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Option Greeks Calculator</CardTitle>
          <CardDescription>
            Calculate Delta, Gamma, Theta, Vega, and Rho for options
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="type">Option Type</Label>
            <Select value={optionType} onValueChange={setOptionType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="call">Call</SelectItem>
                <SelectItem value="put">Put</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="spot">Spot Price ($)</Label>
            <Input
              id="spot"
              type="number"
              placeholder="100"
              value={spotPrice}
              onChange={(e) => setSpotPrice(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="strike">Strike Price ($)</Label>
            <Input
              id="strike"
              type="number"
              placeholder="105"
              value={strikePrice}
              onChange={(e) => setStrikePrice(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="time">Time to Expiry (days)</Label>
            <Input
              id="time"
              type="number"
              placeholder="30"
              value={timeToExpiry}
              onChange={(e) => setTimeToExpiry(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="vol">Implied Volatility (%)</Label>
            <Input
              id="vol"
              type="number"
              placeholder="25"
              value={volatility}
              onChange={(e) => setVolatility(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rate">Risk-Free Rate (%)</Label>
            <Input
              id="rate"
              type="number"
              placeholder="5"
              value={riskFreeRate}
              onChange={(e) => setRiskFreeRate(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 space-y-2">
              <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
                <div>
                  <strong>Delta (Δ):</strong> {result.delta.toFixed(4)}
                  <p className="text-xs text-muted-foreground">Price sensitivity to spot</p>
                </div>
                <CopyResultButton value={result.delta.toFixed(4)} />
              </div>
              <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
                <div>
                  <strong>Gamma (Γ):</strong> {result.gamma.toFixed(6)}
                  <p className="text-xs text-muted-foreground">Rate of change of delta</p>
                </div>
                <CopyResultButton value={result.gamma.toFixed(6)} />
              </div>
              <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
                <div>
                  <strong>Theta (Θ):</strong> {result.theta.toFixed(4)}
                  <p className="text-xs text-muted-foreground">Time decay (per day)</p>
                </div>
                <CopyResultButton value={result.theta.toFixed(4)} />
              </div>
              <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
                <div>
                  <strong>Vega (ν):</strong> {result.vega.toFixed(4)}
                  <p className="text-xs text-muted-foreground">Sensitivity to volatility (1%)</p>
                </div>
                <CopyResultButton value={result.vega.toFixed(4)} />
              </div>
              <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
                <div>
                  <strong>Rho (ρ):</strong> {result.rho.toFixed(4)}
                  <p className="text-xs text-muted-foreground">Sensitivity to interest rate (1%)</p>
                </div>
                <CopyResultButton value={result.rho.toFixed(4)} />
              </div>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["option-greeks"]} trigger={result?.delta} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
